
import React, { useState } from "react";

const TenantWorkflowTable = ({
  workflows,
  loading,
  rolesList,
  onEdit,
  onDelete
}) => {
  const [selectedWorkflow, setSelectedWorkflow] =
    useState(null);

  const getWorkflowTypeLabel = (value) => {
    const options = {
      TENANT_VERIFICATION: "Tenant Verification",
      TEMPLATE_APPROVAL: "Template Approval",
      DOCUMENT_APPROVAL: "Document Approval",
      PROJECT_APPROVAL: "Project Approval",
      GENERAL_APPROVAL: "General Approval"
    };

    return options[value] || value || "-";
  };

  const getStatusClass = (status) => {
    if (status === "COMPLETED") {
      return "completed";
    }

    if (status === "IN_PROGRESS") {
      return "pending";
    }

    if (status === "REJECTED") {
      return "inactive";
    }

    if (status === "CANCELLED") {
      return "inactive";
    }

    return "inactive";
  };

  const getStatusLabel = (status) => {
    const options = {
      IN_PROGRESS: "In Progress",
      COMPLETED: "Completed",
      REJECTED: "Rejected",
      CANCELLED: "Cancelled"
    };

    return options[status] || status || "-";
  };

  const getDecisionClass = (decision) => {
    if (decision === "APPROVED") {
      return "completed";
    }

    if (decision === "REJECTED") {
      return "inactive";
    }

    if (decision === "RETURNED") {
      return "pending";
    }

    return "pending";
  };

  const getDecisionLabel = (decision) => {
    const options = {
      "": "Pending",
      PENDING: "Pending",
      APPROVED: "Approved",
      REJECTED: "Rejected",
      RETURNED: "Returned"
    };

    return options[decision] || decision || "Pending";
  };

  const getRoleName = (roleId) => {
    if (!roleId) {
      return "-";
    }

    const role = rolesList?.find(
      (item) =>
        String(item.id) === String(roleId)
    );

    return (
      role?.name ||
      role?.role_name ||
      role?.code ||
      `Role ${roleId}`
    );
  };

  const formatDateTime = (date) => {
    if (!date) {
      return "-";
    }

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return date;
    }

    return parsedDate.toLocaleString("en-GB");
  };

  if (loading) {
    return (
      <div className="workflow-loading">
        Loading workflows...
      </div>
    );
  }

  if (!workflows || workflows.length === 0) {
    return (
      <div className="workflow-empty">
        <div className="workflow-empty-icon">
          +
        </div>

        <h3>No Workflows Found</h3>

        <p>
          There are no workflows available for
          the current tenant.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="workflow-table-wrapper">
        <table className="workflow-table">
          <thead>
            <tr>
              <th>Workflow Type</th>
              <th>Reference Table</th>
              <th>Reference ID</th>
              <th>Steps</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {workflows.map((workflow) => (
              <tr key={workflow.id}>
                <td>
                  <div className="workflow-name-cell">
                    <div className="workflow-avatar">
                      {getWorkflowTypeLabel(
                        workflow.workflow_type
                      )
                        .charAt(0)
                        .toUpperCase()}
                    </div>

                    <div>
                      <strong>
                        {getWorkflowTypeLabel(
                          workflow.workflow_type
                        )}
                      </strong>

                      <span>
                        Workflow #{workflow.id}
                      </span>
                    </div>
                  </div>
                </td>

                <td>
                  <span className="workflow-type-pill">
                    {workflow.reference_table ||
                      "-"}
                  </span>
                </td>

                <td>
                  <span
                    style={{
                      display: "block",
                      maxWidth: "220px",
                      overflow: "hidden",
                      textOverflow:
                        "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                    title={
                      workflow.reference_id
                    }
                  >
                    {workflow.reference_id ||
                      "-"}
                  </span>
                </td>

                <td>
                  <span className="workflow-type-pill">
                    {Array.isArray(
                      workflow.steps
                    )
                      ? workflow.steps.length
                      : 0}
                  </span>
                </td>

                <td>
                  <span
                    className={`workflow-status ${getStatusClass(
                      workflow.status
                    )}`}
                  >
                    {getStatusLabel(
                      workflow.status
                    )}
                  </span>
                </td>

                <td>
                  <div className="workflow-actions">
                    <button
                      type="button"
                      className="workflow-view-btn"
                      onClick={() =>
                        setSelectedWorkflow(
                          workflow
                        )
                      }
                    >
                      View
                    </button>

                    <button
                      type="button"
                      className="workflow-edit-btn"
                      onClick={() =>
                        onEdit(workflow)
                      }
                    >
                      Edit
                    </button>

                    <button
                      type="button"
                      className="workflow-delete-btn"
                      onClick={() =>
                        onDelete(workflow)
                      }
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedWorkflow && (
        <div
          className="workflow-modal-overlay"
          onClick={() =>
            setSelectedWorkflow(null)
          }
        >
          <div
            className="workflow-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
          >
            <div className="workflow-modal-header">
              <div>
                <h3>
                  Workflow Details
                </h3>

                <p>
                  Complete workflow and step
                  information
                </p>
              </div>

              <button
                type="button"
                className="workflow-modal-close"
                onClick={() =>
                  setSelectedWorkflow(
                    null
                  )
                }
              >
                ×
              </button>
            </div>

            <div className="workflow-modal-body">
              <div className="workflow-detail-section">
                <div className="workflow-detail-section-title">
                  Workflow Information
                </div>

                <div className="workflow-detail-grid">
                  <div className="workflow-detail-item">
                    <span>
                      Workflow Type
                    </span>

                    <strong>
                      {getWorkflowTypeLabel(
                        selectedWorkflow.workflow_type
                      )}
                    </strong>
                  </div>

                  <div className="workflow-detail-item">
                    <span>Status</span>

                    <strong>
                      <span
                        className={`workflow-status ${getStatusClass(
                          selectedWorkflow.status
                        )}`}
                      >
                        {getStatusLabel(
                          selectedWorkflow.status
                        )}
                      </span>
                    </strong>
                  </div>

                  <div className="workflow-detail-item">
                    <span>
                      Reference Table
                    </span>

                    <strong>
                      {selectedWorkflow.reference_table ||
                        "-"}
                    </strong>
                  </div>

                  <div className="workflow-detail-item">
                    <span>
                      Reference ID
                    </span>

                    <strong>
                      {selectedWorkflow.reference_id ||
                        "-"}
                    </strong>
                  </div>

                  <div className="workflow-detail-item">
                    <span>
                      Workflow ID
                    </span>

                    <strong>
                      {selectedWorkflow.id ||
                        "-"}
                    </strong>
                  </div>

                  <div className="workflow-detail-item">
                    <span>
                      Tenant ID
                    </span>

                    <strong>
                      {selectedWorkflow.tenant ||
                        "-"}
                    </strong>
                  </div>
                </div>
              </div>

              <div className="workflow-detail-section">
                <div
                  className="workflow-detail-section-title"
                  style={{
                    display: "flex",
                    justifyContent:
                      "space-between",
                    alignItems: "center"
                  }}
                >
                  <span>
                    Workflow Steps
                  </span>

                  <span className="workflow-type-pill">
                    {Array.isArray(
                      selectedWorkflow.steps
                    )
                      ? selectedWorkflow
                          .steps.length
                      : 0}
                  </span>
                </div>

                {!Array.isArray(
                  selectedWorkflow.steps
                ) ||
                selectedWorkflow.steps.length ===
                  0 ? (
                  <div className="workflow-no-data">
                    No workflow steps
                    available.
                  </div>
                ) : (
                  <div className="workflow-step-details-list">
                    {selectedWorkflow.steps.map(
                      (step, index) => (
                        <div
                          className="workflow-step-detail-card"
                          key={
                            step.id ||
                            index
                          }
                        >
                          <div className="workflow-step-detail-header">
                            <strong>
                              Step{" "}
                              {step.step_order ||
                                index + 1}
                            </strong>

                            <span
                              className={`workflow-status ${getDecisionClass(
                                step.decision
                              )}`}
                            >
                              {getDecisionLabel(
                                step.decision
                              )}
                            </span>
                          </div>

                          <div className="workflow-detail-grid">
                            <div className="workflow-detail-item">
                              <span>
                                Step Order
                              </span>

                              <strong>
                                {step.step_order ||
                                  "-"}
                              </strong>
                            </div>

                            <div className="workflow-detail-item">
                              <span>
                                Assigned Role
                              </span>

                              <strong>
                                {getRoleName(
                                  step.assigned_role
                                )}
                              </strong>
                            </div>

                            <div className="workflow-detail-item">
                              <span>
                                Decision
                              </span>

                              <strong>
                                {getDecisionLabel(
                                  step.decision
                                )}
                              </strong>
                            </div>

                            <div className="workflow-detail-item">
                              <span>
                                Step ID
                              </span>

                              <strong>
                                {step.id ||
                                  "-"}
                              </strong>
                            </div>

                            <div className="workflow-detail-item">
                              <span>
                                Actioned By
                              </span>

                              <strong>
                                {step.actioned_by ||
                                  "-"}
                              </strong>
                            </div>

                            <div className="workflow-detail-item">
                              <span>
                                Actioned At
                              </span>

                              <strong>
                                {formatDateTime(
                                  step.actioned_at
                                )}
                              </strong>
                            </div>

                            <div
                              className="workflow-detail-item"
                              style={{
                                gridColumn:
                                  "1 / -1"
                              }}
                            >
                              <span>
                                Comments
                              </span>

                              <strong>
                                {step.comments ||
                                  "-"}
                              </strong>
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="workflow-modal-footer">
              <button
                type="button"
                className="workflow-cancel-btn"
                onClick={() =>
                  setSelectedWorkflow(
                    null
                  )
                }
              >
                Close
              </button>

              <button
                type="button"
                className="workflow-edit-btn"
                onClick={() => {
                  const workflow =
                    selectedWorkflow;

                  setSelectedWorkflow(
                    null
                  );

                  onEdit(workflow);
                }}
              >
                Edit Workflow
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TenantWorkflowTable;

